import { useEffect, useState } from "react";
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFile, IconTrash } from "@tabler/icons-react";
import { File as OrderFile } from "@jasper/client-models";
import { joinSocketRoom, onSocketEvent, orderFileRoom } from "@jasper/shared";
import { useOrderFiles } from "@jasper/use-cases";
import { FileDropzone } from "/@/components/order/FileDropzone";
import { isImageFile, pluralize } from "./review.utils";

const AddedFile = ({
  file,
  onRemove,
}: {
  file: OrderFile;
  onRemove: () => void;
}) => {
  return (
    <Stack gap="xs">
      <AspectRatio ratio={4 / 3}>
        {isImageFile(file.filename) ? (
          <Image src={file.url} alt={file.filename} radius="md" />
        ) : (
          <ThemeIcon variant="light" radius="md" aria-hidden>
            <IconFile />
          </ThemeIcon>
        )}
      </AspectRatio>
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Text size="sm" truncate="end">
          {file.filename}
        </Text>
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={`Supprimer ${file.filename}`}
          onClick={onRemove}
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Group>
    </Stack>
  );
};

// Files attached to the order, whether they are added here or sent from the phone through the QR code
export const OrderFilesField = ({ orderId }: { orderId: string }) => {
  const { files, uploadOrderFile, removeOrderFile, refetchOrder } =
    useOrderFiles({ orderId });
  const [isUploading, setIsUploading] = useState(false);

  // Files sent from the phone are announced through the order file socket, the order is refetched to show them
  useEffect(() => {
    const leaveRoom = joinSocketRoom(orderFileRoom(orderId));
    const unsubscribe = onSocketEvent("clientOrderFile", () => {
      void refetchOrder();
    });

    return () => {
      unsubscribe();
      leaveRoom();
    };
  }, [orderId]);

  const addFiles = async (newFiles: File[]) => {
    setIsUploading(true);
    const results = await Promise.allSettled(
      newFiles.map(file => uploadOrderFile(file))
    );
    setIsUploading(false);

    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    if (failures.length > 0) {
      console.error(
        "Error while uploading order files",
        failures.map(failure => failure.reason)
      );
      notifications.show({
        color: "red",
        title: "Erreur lors de l'ajout des fichiers",
        message: `${pluralize(failures.length, "fichier n'a pas pu être ajouté", "fichiers n'ont pas pu être ajoutés")}. Veuillez réessayer.`,
      });
    }
  };

  const removeFile = (file: OrderFile) =>
    removeOrderFile(file.id).catch(error => {
      console.error("Error while removing an order file", error);
      notifications.show({
        color: "red",
        title: "Erreur lors de la suppression",
        message: `${file.filename} n'a pas pu être supprimé. Veuillez réessayer.`,
      });
    });

  return (
    <Stack gap="md">
      <FileDropzone
        description="photos, PDF, STL · glisser-déposer"
        isLoading={isUploading}
        onDrop={addFiles}
      />

      {files.length > 0 && (
        <Stack gap="xs">
          <Group gap="xs">
            <Text variant="eyebrow" c="dimmed">
              Fichiers ajoutés
            </Text>
            <Badge variant="light" size="sm">
              {files.length}
            </Badge>
          </Group>
          <SimpleGrid cols={3} spacing="sm">
            {files.map(file => (
              <AddedFile
                key={file.id}
                file={file}
                onRemove={() => removeFile(file)}
              />
            ))}
          </SimpleGrid>
        </Stack>
      )}
    </Stack>
  );
};
